<template>
  <v-flex>
    <v-layout row wrap>
      <v-flex xs12>
        <h2 class="primary--text pb-2">Scripts</h2>
        <p>Below are the list of functions currently available for the {{caseType.name}} template.</p>
      </v-flex>
      <v-flex xs12 class="mb-3">
        <v-btn
          class="ml-0 mt-0 mb-3"
          color="secondary"
          v-show="addScriptButtons"
          v-on:click="addScriptForm = true; addScriptButtons = false">
          Add a script
        </v-btn>
        <v-btn
          class="ml-0 mt-0 mb-3"
          color="secondary"
          v-show="addScriptButtons"
          v-on:click="addLifecycleForm = true; addScriptButtons = false">
          Add a Lifecycle Function
        </v-btn>
        <form v-show="addScriptForm">
          <v-layout wrap row class="pa-4 mb-3" style="background:rgb(240, 240, 240);">
            <v-flex xs12 sm6 mt-2>
              <v-text-field label="Script name" class="white-box-field" v-model="newName" box></v-text-field>
              <p style="margin-top:-12px">
                Access this function on the url -
                <b>/{{ newName }}</b>
              </p>
              <v-textarea box label="Script description" class="white-box-field mt-3" v-model="newDescription"></v-textarea>
              <v-switch color="secondary" v-model="newIsReusable" style="margin-top:-12px">
                <template v-slot:label>
                  Make this a library function
                  <v-tooltip top color="secondary">
                    <v-icon style="vertical-align:middle" color="secondary"  class="ml-2" slot="activator">help</v-icon>
                    <span>Text explaining this toggle</span>
                  </v-tooltip>
                </template>
              </v-switch>
              <p v-if="newIsReusable">
                Reference this function with -
                <b>fn.benefit.{{ newName }}.main(...)</b>
              </p>
            </v-flex>
            <v-flex xs12>
              <v-btn class="ml-0 mr-0" color="success" @click="createScript()">Save &amp; Compose</v-btn>
              <v-btn color="error" v-on:click="addScriptForm = false; addScriptButtons = true">
                Cancel
              </v-btn>
            </v-flex>
          </v-layout>
        </form>
        <form v-show="addLifecycleForm">
          <v-layout wrap row class="pa-4 mb-3" style="background:rgb(240, 240, 240);">
            <v-flex xs12 sm6 mt-2>
              <v-autocomplete
                box dense label="Lifecycle Functions"
                :items="lifecycleFunctions" v-model="newName"
                append-icon="keyboard_arrow_down"
                class="white-box-field">
              </v-autocomplete>
              <p v-if="newName" style="margin-top:-12px">
                Access this function on the url -
                <b>/{{ newName }}</b>
              </p>
              <v-textarea
                v-if="newName" box
                v-model="newDescription"
                label="Lifecycle description"
                class="white-box-field mt-3">
              </v-textarea>
              <v-switch v-if="newName" color="secondary" v-model="newIsReusable" style="margin-top:-12px">
                <template v-slot:label>
                  Make this a library function
                  <v-tooltip top color="secondary">
                    <v-icon style="vertical-align:middle" color="secondary"  class="ml-2" slot="activator">help</v-icon>
                    <span>Text explaining this toggle</span>
                  </v-tooltip>
                </template>
              </v-switch>

              <p v-if="newName">
                Reference this function with -
                <b>fn.casetype.{{ newName }}.main(...)</b>
              </p>
            </v-flex>
            <v-flex xs12>
              <v-btn class="ml-0 mr-0" color="success" @click="createScript()" >Save &amp; Compose</v-btn>
              <v-btn color="error" v-on:click="addLifecycleForm = false; addScriptButtons = true">Cancel</v-btn>
            </v-flex>
          </v-layout>
        </form>
        <v-layout
          mt-0 row wrap
          class="script-card-container card-margin-fix">
          <ScriptCard
            class="mr-0"
            v-for="(script) in caseScripts" :key="script.id"
            :script="script"
            v-on:removeScript="remove(script)">
          </ScriptCard>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<script>
import ScriptCard from "@/components/cases/case-config/ScriptCard.vue"
import { sampleForRuntime } from "@/components/cases/case-config/scripts/scriptSamples.js"

export default {
  name: "case-config-scripts",
  components: { ScriptCard },
  props: { caseType: { type: Object, required: true } },
  data() {
    var defaultSample = sampleForRuntime("javascript")
    return {
      addScriptButtons: true,
      addScriptForm: false,
      addLifecycleForm: false,
      lifecycleFunctions: ["preCreate", "postCreate", "preSave", "postSave", "preDelete", "postDelete", "preStateChange", "postStateChange", "caseFieldsUpdated"],
      newName: "",
      newDescription: "",
      newDraftCode: defaultSample,
      newPublishedCode: defaultSample,
      newDraftDocument: '{"title": "Sample Document"}',
      newIsReusable: false,
      newIsMicroservice: false
    }
  },
  computed: {
    caseScripts() { return this.caseType.scripts },
    scripts() {
      const scripts = []
      const functions = this.lifecycleFunctions
      if (this.caseScripts) {
        this.caseScripts.forEach(function(script) {
          if (!functions.includes(script.name)) { scripts.push(script) }
        })
      }
      return scripts
    },
    lifeCycles() {
      const lifecycles = []
      const functions = this.lifecycleFunctions
      if (this.caseScripts) {
        this.caseScripts.forEach(function(script) {
          if (functions.includes(script.name)) { lifecycles.push(script) }
        })
      }
      return lifecycles
    }
  },
  methods: {
    async createScript() {
      this.addScriptForm = false
      this.addLifecycleForm = false
      this.addScriptButtons = true
      const newScript = {
        caseTypeId: this.caseType.id,
        name: this.newName,
        description: this.newDescription,
        draftCode: this.newDraftCode,
        draftDocument: this.newDraftDocument,
        publishedCode: this.newPublishedCode,
        reusable: this.newIsReusable,
        isMicroservice: this.newIsMicroservice,
        runtimeType: "faas",
        runtimeLanguage: "javascript",
        runtimeImage: null
      }
      const result = await this.$store.dispatch("createScript", newScript)
      if (!result.error) {
        this.newName = ""
        this.newDescription = ""
        this.newIsReusable = false
      }
    },
    async remove(script) {
      await this.$store.dispatch("deleteScript", { caseTypeId: this.caseType.id, scriptId: script.id })
    }
  }
  /* Need to get this height match working for my maximise button
    calculateHeight() {
      this.$el
        .getElementsByClassName("v-tabs")
        .item(0).clientHeight = this.$el
        .getElementsByClassName("maximised")
        .item(0).clientHeight;
    }
  mounted() {
    this.calculateHeight();
  } */

  /* Also need to figure out how to trigger a resize. The slider bar under the selected tab (When tabs within tabs) doesn't appear until tab clicked or page resizes - https://codepen.io/Ondis99/pen/MGrRvj this isn't working */
}
</script>

<style>
.maximise-btn,
.minimise-btn {
  display: none;
}

.v-expansion-panel__container--active.maximised {
  background: rgb(240, 240, 240);
  position: absolute !important;
  top: 4px !important;
  left: 3px !important;
  width: calc(100% - 6px) !important;
  height: calc(100% - 7px) !important;
  z-index: 22 !important;
  box-shadow: 0 0 0 0 !important;
}

.v-expansion-panel__container--active .maximise-btn {
  display: block !important;
  opacity: 1;
  right: 35px !important;
  transition: all 0s !important;
}

.v-expansion-panel__container--active .lifecycle-label {
  display: none;
}

.v-expansion-panel__container--active.maximised .maximise-btn {
  display: none !important;
}

.v-expansion-panel__container--active.maximised .minimise-btn {
  display: block !important;
  right: 0px !important;
}

.expansion-header {
  padding: 0 0 0 25px;
}

.v-expansion-panel__container--active.maximised .expansion-header {
  padding: 0;
}

.v-expansion-panel__container--active.maximised
  .v-expansion-panel__header__icon {
  display: none;
}

.v-expansion-panel__container--active.maximised .remove-script-btn {
  display: none;
}

.list {
  width: 100% !important;
  display: block !important;
  padding: 0 !important;
  flex-basis: 100% !important;
  max-width: 100% !important;
}
</style>
